/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState, useRef, useMemo } from 'react';

interface StreamingTextProps {
  text: string;
  isStreaming: boolean;
  speed?: number; // milliseconds per character (base speed)
  maxAnimationDuration?: number; // maximum time in ms for animation (default 5000ms)
  // Fires once the full (final, non-streaming) text has finished animating.
  onComplete?: () => void;
  children: (
    displayedText: string,
    isAnimating: boolean,
    skipAnimation: () => void,
  ) => React.ReactNode;
}

// Calculate adaptive speed based on message length
const calculateAdaptiveSpeed = (
  charCount: number,
  baseSpeed: number,
  maxDuration: number,
): number => {
  // Short messages (< 200 chars): use base speed
  if (charCount < 200) {
    return baseSpeed;
  }

  // Long messages: calculate speed to fit within maxDuration
  const targetSpeed = maxDuration / charCount;

  // Don't go faster than 5ms per character (too fast to see)
  const minSpeed = 5;

  return Math.max(minSpeed, Math.min(baseSpeed, targetSpeed));
};

export const StreamingText: React.FC<StreamingTextProps> = ({
  text,
  isStreaming,
  speed = 40,
  maxAnimationDuration = 10000,
  onComplete,
  children,
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [skipRequested, setSkipRequested] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const currentIndexRef = useRef<number>(0);
  const previousTextRef = useRef<string>('');
  const adaptiveSpeedRef = useRef<number>(speed);
  const isInitialMountRef = useRef(true);
  const onCompleteRef = useRef(onComplete);
  const completeFiredForRef = useRef<string | null>(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Memoized function to skip animation and show all text immediately
  const skipAnimation = useMemo(
    () => () => {
      setSkipRequested(true);
    },
    [],
  );

  useEffect(() => {
    // On initial mount with non-streaming text, start animation immediately
    if (isInitialMountRef.current && !isStreaming && text.length > 0) {
      isInitialMountRef.current = false;
      previousTextRef.current = text;

      // Start character-by-character animation
      adaptiveSpeedRef.current = calculateAdaptiveSpeed(text.length, speed, maxAnimationDuration);
      currentIndexRef.current = 0;
      setIsAnimating(true);

      const displayNextChar = () => {
        if (currentIndexRef.current < text.length) {
          const charsToShow = text.slice(0, currentIndexRef.current + 1);
          setDisplayedText(charsToShow);
          currentIndexRef.current += 1;

          if (currentIndexRef.current < text.length) {
            timeoutRef.current = window.setTimeout(displayNextChar, adaptiveSpeedRef.current);
          } else {
            setIsAnimating(false);
          }
        }
      };

      timeoutRef.current = window.setTimeout(displayNextChar, adaptiveSpeedRef.current);
      return;
    }

    // Handle skip animation request - only skip if explicitly requested
    if (skipRequested && text.length > 0) {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setDisplayedText(text);
      currentIndexRef.current = text.length;
      setIsAnimating(false);
      setSkipRequested(false);
      return;
    }

    // If text is empty, reset everything using derived state check
    if (text.length === 0 && previousTextRef.current.length > 0) {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      previousTextRef.current = '';
      currentIndexRef.current = 0;
      return;
    }

    // Only process if text has changed and grown
    if (text === previousTextRef.current || text.length < previousTextRef.current.length) {
      return;
    }

    previousTextRef.current = text;

    // Calculate adaptive speed based on character count
    adaptiveSpeedRef.current = calculateAdaptiveSpeed(text.length, speed, maxAnimationDuration);

    // If we have new characters to display
    if (currentIndexRef.current < text.length) {
      setIsAnimating(true);

      const displayNextChar = () => {
        if (currentIndexRef.current < text.length) {
          const charsToShow = text.slice(0, currentIndexRef.current + 1);
          setDisplayedText(charsToShow);
          currentIndexRef.current += 1;

          if (currentIndexRef.current < text.length) {
            timeoutRef.current = window.setTimeout(displayNextChar, adaptiveSpeedRef.current);
          } else {
            setIsAnimating(false);
          }
        }
      };

      // If we're starting fresh or resuming, begin the animation
      if (currentIndexRef.current === 0 || !timeoutRef.current) {
        timeoutRef.current = window.setTimeout(displayNextChar, adaptiveSpeedRef.current);
      }
    }

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [text, speed, maxAnimationDuration, skipRequested, isStreaming, displayedText.length]);

  // Fire onComplete once the full, final (non-streaming) text is fully shown.
  useEffect(() => {
    if (!isStreaming && text.length > 0 && displayedText === text && !isAnimating) {
      if (completeFiredForRef.current !== text) {
        completeFiredForRef.current = text;
        onCompleteRef.current?.();
      }
    }
  }, [isStreaming, text, displayedText, isAnimating]);

  // Derive empty state from text prop
  const shouldShowEmpty = text.length === 0;
  const finalDisplayedText = shouldShowEmpty ? '' : displayedText;

  return <>{children(finalDisplayedText, isAnimating || isStreaming, skipAnimation)}</>;
};
